import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, List, X, Pulse } from "@phosphor-icons/react";

export const BrandMark = ({ className = "", tone = "light" }) => (
  <Link to="/" className={`inline-flex items-center gap-2 group ${className}`} data-testid="brand-logo">
    <span className="relative inline-flex h-14 w-14 items-center justify-center rounded-md bg-transparent overflow-hidden shrink-0">
      <img src="/logo.png" alt="MedexOVA logo" className="h-14 w-14 object-contain logo-glow" />
    </span>
    <span className={`font-heading text-xl tracking-tight leading-none ${tone === "dark" ? "text-white" : "text-slate_navy"}`}>
      <span className="font-black text-[#16A34A]">Medex</span><span className="font-black text-ova">OVA</span>
      <span className={`block text-[10px] uppercase tracking-[0.3em] font-semibold ${tone === "dark" ? "text-white/50" : "text-slate-400"} mt-0.5`}>
        Nationwide NEMT Facilitator
      </span>
    </span>
  </Link>
);

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [pathname]);

  const navLinks = [
    { href: "/#services", label: "Services" },
    { href: "/#who-we-serve", label: "Who We Serve" },
    { href: "/partners", label: "Partners", routerLink: true },
    { href: "/#contact", label: "Contact" },
  ];

  return (
    <header
      className={`sticky top-0 z-50 transition-all ${
        scrolled ? "backdrop-blur-xl bg-white/75 border-b border-black/5 shadow-[0_1px_0_rgba(11,27,51,0.04)]" : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
        <BrandMark />
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((l) => (
            l.routerLink ? (
              <Link
                key={l.href}
                to={l.href}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="relative text-sm text-slate-700 hover:text-brandblue transition-colors group"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-[2px] w-0 bg-brandblue group-hover:w-full transition-all duration-300"></span>
              </Link>
            ) : (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="relative text-sm text-slate-700 hover:text-brandblue transition-colors group"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-[2px] w-0 bg-brandblue group-hover:w-full transition-all duration-300"></span>
              </a>
            )
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <span className="hidden sm:inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-500">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-forest/60 live-dot"></span>
              <span className="relative inline-flex h-2 w-2 rounded-full bg-forest"></span>
            </span>
            Dispatch live
          </span>
          <Link
            to="/book"
            data-testid="header-book-trip-btn"
            className="hidden sm:inline-flex items-center gap-2 rounded-md bg-brandblue-ink hover:bg-brandblue-deep text-white px-4 py-2.5 text-sm font-medium transition-colors"
          >
            Book a Trip <ArrowRight weight="bold" size={14} />
          </Link>
          <button
            className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-md border border-black/10"
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {mobileOpen ? <X size={18} /> : <List size={18} />}
          </button>
        </div>
      </div>
      {mobileOpen && (
        <div className="md:hidden border-t border-black/5 bg-white/95 backdrop-blur-xl">
          <div className="px-6 py-4 flex flex-col gap-3">
            {navLinks.map((l) => (
              <a key={l.href} href={l.href} className="py-2 text-slate-700">
                {l.label}
              </a>
            ))}
            <Link to="/book" className="mt-2 rounded-md bg-brandblue-ink text-white px-4 py-2 text-center">
              Book a Trip
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
